<template>
  <div class="profile-form">
    <div class="col-1">
      <div v-if="error" class="form-error">{{ error.message || error.error }}</div>
      <md-progress-bar v-if="loading" md-mode="query" />
      <div>
        <div v-if="verificationEmailSent">Verification email sent to new address</div>
      </div>
      <label class="blue-6 color">First Name</label>
      <md-field>
        <md-input v-model.trim="state.givenName" placeholder="First Name" />
      </md-field>
      <label class="blue-6 color">Last Name</label>
      <md-field>
        <md-input v-model.trim="state.familyName" placeholder="Last Name" />
      </md-field>
      <label class="blue-6 color">Email</label>
      <md-field>
        <md-input v-model.trim="state.email" placeholder="Email" />
      </md-field>
      <div class="md-layout md-alignment-center-right">
        <md-button class="md-raised md-primary" @click="checkEmailChanged">Save</md-button>
      </div>
      <div v-if="invites.length > 0" class="heading">
        <span>Invitations</span>
      </div>
      <div>
        <md-card v-for="invite in invites" :key="invite._id" class="profile-card">
          <md-card-header>
            <div class="md-title">Pending Invitation from {{ invite.org_name }}</div>
          </md-card-header>
          <md-card-content v-if="invite.loading || invite.error">
            <md-progress-bar v-if="invite.loading" md-mode="indeterminate" />
            <div v-if="invite.error" class="red-3 color">{{ invite.error }}</div>
          </md-card-content>
          <md-card-actions v-if="!invite.loading">
            <div>
              <md-button class="md-raised md-accent" @click="dismiss(invite)">Dismiss</md-button>
              <md-button class="md-raised green-5 bg" @click="accept(invite)">Accept</md-button>
            </div>
          </md-card-actions>
        </md-card>
      </div>
      <div class="heading">
        <span>{{ orgHeading }}</span>
      </div>
      <div>
        <md-card v-for="org in orgGroups" :key="org._id" class="profile-card org-leave-card">
          <md-card-header>
            <div class="md-title">{{ org.name }}</div>
          </md-card-header>
          <md-card-content v-if="org.leaving || org.leaveError">
            <md-progress-bar v-if="org.leaving" md-mode="indeterminate" />
            <div v-if="org.leaveError" class="red-3 color">{{ org.leaveError.message || org.leaveError.error }}</div>
          </md-card-content>
          <md-card-actions>
            <div class="leave-button-container">
              <md-button
                :disabled="!canLeaveOrg(org)"
                class="md-raised md-accent"
                @click="openOrgLeaveConfirm(org)"
              >
                Leave
              </md-button>
              <md-tooltip v-if="!canLeaveOrg(org)">
                You can't leave an organization you are a key owner of
              </md-tooltip>
            </div>
          </md-card-actions>
        </md-card>
      </div>
    </div>
    <div v-if="isInDevOrg && isSuperAdmin" class="col-2">
      <md-menu>
        <md-button md-menu-trigger class="md-primary share-button" :style="{ color: 'rgba(0,0,0,0.87)' }">
          <md-icon class="share-icon" label="Download">file_download</md-icon>
          Summaries
        </md-button>
        <md-menu-content>
          <md-menu-item @click="fetchSummaryOwn">Scenario Summary (self)</md-menu-item>
          <md-menu-item @click="fetchSummaryAll">Scenario Summary (all members)</md-menu-item>
        </md-menu-content>
        <md-tooltip>Download CSV summary of your scenarios for all of your orgs</md-tooltip>
      </md-menu>
    </div>

    <!-- Dialogs -->
    <md-dialog-confirm
      class="confirm-dialog"
      :md-active.sync="state.confirmDialogOpen"
      md-title="Email Change"
      md-content="Are you sure you want to change your email? This action will log you out of the system."
      md-confirm-text="Accept"
      md-cancel-text="Cancel"
      @md-confirm="confirm()"
    />

    <md-dialog class="centered" :md-active.sync="state.dismissDialogOpen" :md-fullscreen="false">
      <md-dialog-content>
        <span>Dismissing this invitation will remove your ability to access this Organization’s case data.  You will need to be re-invited by the account administrator if you wish to gain access.</span>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-raised md-accent" @click="closeDismissDialog">Don't Dismiss</md-button>
        <md-button class="md-raised green-5 bg" @click="onConfirmDismiss">Dismiss Invitation</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog class="org-leave-dialog centered" :md-active.sync="state.orgLeaveDialogOpen" :md-fullscreen="false">
      <md-dialog-content>
        <span>Are you sure you want to leave?</span>
        <br>
        <br>
        <span class="org-name">{{ state.orgLeavePendingOrg && state.orgLeavePendingOrg.name }}</span>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-raised md-accent" @click="closeOrgLeaveDialog">Cancel</md-button>
        <md-button class="md-raised green-5 bg" @click="onConfirmLeaveOrg">Confirm</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
export default {
  name: 'profile-form',
  props: {
    loading: {
      type: Boolean,
    },
    email: {
      type: String,
      default: '',
    },
    givenName: {
      type: String,
      default: '',
    },
    familyName: {
      type: String,
      default: '',
    },
    error: {
      type: Object,
      default: null,
    },
    verificationEmailSent: {
      type: Boolean,
    },
    invites: {
      type: Array,
      default: () => [],
    },
    user: {
      type: Object,
      required: true,
    },
    orgGroups: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    state: {
      email: '',
      givenName: '',
      familyName: '',
      confirmDialogOpen: false,
      dismissDialogOpen: false,
      orgLeaveDialogOpen: false,
      orgLeavePendingOrg: null,
      inviteToDismiss: null,
    }
  }),
  computed: {
    orgHeading() {
      return `You are currently a member of ${this.orgGroups.length}
              organization${this.orgGroups.length !== 1 ? 's' : ''}`;
    },
    isInDevOrg() {
      return !!this.orgGroups.find(org => org.metadata.dev);
    },
    isSuperAdmin() {
      return this.user.activeRole === 'SUPER_ADMIN';
    }
  },
  created() {
      this.state.email = this.email;
      this.state.givenName = this.givenName;
      this.state.familyName = this.familyName;
  },
  methods: {
    checkEmailChanged() {
      if (this.state.email !== this.email) {
        this.state.confirmDialogOpen = true;
      }
      else {
        this.save();
      }
    },
    save() {
      const { email, givenName, familyName } = this.state;
      const user = {
        ...(email !== this.email) && { email },
        ...(givenName !== this.givenName) && { given_name: givenName },
        ...(familyName !== this.familyName) && { family_name: familyName },
      }
      if (Object.keys(user).length === 0) {
        return;
      }
      this.$emit('save', user);
    },
    confirm() {
      this.state.confirmDialogOpen = false;
      this.save();
    },
    closeModal() {
      this.state.confirmDialogOpen = false;
    },
    dismiss(invite) {
      this.state.inviteToDismiss = invite;
      this.state.dismissDialogOpen = true;
    },
    accept(invite) {
      this.$emit('accept', invite._id);
    },
    closeDismissDialog() {
      this.state.dismissDialogOpen = false;
    },
    onConfirmDismiss() {
      this.closeDismissDialog();
      this.$emit('dismiss', this.state.inviteToDismiss._id);
      this.state.inviteToDismiss = null;
    },
    canLeaveOrg(org) {
      return org.metadata.ownerId !== this.user.id;
    },
    openOrgLeaveConfirm(org) {
      this.state.orgLeaveDialogOpen = true;
      this.state.orgLeavePendingOrg = org;
    },
    closeOrgLeaveDialog() {
      this.state.orgLeaveDialogOpen = false;
    },
    onConfirmLeaveOrg() {
      this.$emit('leaveOrg', this.state.orgLeavePendingOrg._id);
      this.closeOrgLeaveDialog();
      this.state.orgLeaveDialogOpen = false;
      this.$analytics.event('Account', 'leave_org', this.state.orgLeavePendingOrg.name);
    },
    fetchSummaryOwn() {
      this.$emit('fetchSummary', false);
    },
    fetchSummaryAll() {
      this.$emit('fetchSummary', true);
    },
  },
};
</script>

<style scoped>
.profile-form {
  width: 65%;
  min-width: 350px;
  display: flex;
  flex-direction: row;
}
.col-1, .col2 {
  display: flex;
  flex-direction: column;
}
.col-1 {
  flex: 1;
}
.heading {
  margin-top: 20px;
  margin-bottom: 6px;
  font-size: 1.2em;
}
.profile-card {
  margin-bottom: 20px;
  margin-left: 0;
  margin-right: 0;
}
.org-leave-card .md-card-actions {
  justify-content: flex-start;
}
.leave-button-container {
  display: inline-block;
}
.org-leave-dialog .org-name {
  font-size: 31px;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
